export const LEVEL_LABELS = {
  LOGGED_WARNING: 'Logged Warning',
  YELLOW: 'Yellow',
  ORANGE: 'Orange',
  RED: 'Red',
};

export const LEVEL_COLORS = {
  LOGGED_WARNING: 'bg-slate-100 text-slate-700 border-slate-300',
  YELLOW: 'bg-amber-100 text-amber-800 border-amber-300',
  ORANGE: 'bg-orange-100 text-orange-800 border-orange-300',
  RED: 'bg-red-100 text-red-800 border-red-300',
};

// A card in one of these statuses is closed: every card action (draft/approve/send-back/
// accept CAP, assign reviewer, record review, verify, acknowledge, appeal, letter, show-cause)
// is rejected by the backend with "This card is closed".
export const CLOSED_CARD_STATUSES = ['ESCALATED', 'CLEARED', 'EXITED', 'REVERTED_TO_ORANGE'];

export function isCardClosed(status) {
  return CLOSED_CARD_STATUSES.includes(status);
}

export function statusLabel(status, card, audience = 'employee') {
  switch (status) {
    case 'HR_REVIEW': return audience === 'md' ? 'Awaiting your review' : 'With HR for review';
    case 'INFO_REQUESTED': return audience === 'md' ? 'Awaiting the reporter\'s reply to your information request' : 'HR asked for more information';
    case 'AWAITING_STATEMENT': return audience === 'employee' ? 'Awaiting your statement' : "Awaiting the employee's statement";
    case 'HR_DECISION': return audience === 'md' ? 'Awaiting your decision' : 'HR is deciding';
    case 'PENDING_MD_APPROVAL': return "Awaiting the MD's decision";
    case 'ISSUED': return 'Card issued';
    case 'DISMISSED': return 'Dismissed — no action taken';
    case 'ACTIVE_CAP': return 'Action plan in progress';
    case 'CLEAN_PERIOD': return (card?.clean_period_ends_at || card?.clears_on) ? `Clean period until ${fmtDateOnly(card.clean_period_ends_at || card.clears_on)}` : 'Clean period in progress';
    case 'CLEARED': return 'Cleared';
    case 'ESCALATED': return card?.escalated_to_level ? `Escalated to ${levelLabel(card.escalated_to_level)}` : 'Escalated to a higher level';
    case 'SHOW_CAUSE': return 'Show-cause notice sent';
    case 'EXITED': return 'Exit decided';
    case 'REVERTED_TO_ORANGE': return 'Reverted to Orange';
    default: return status;
  }
}

export function levelLabel(level) {
  return LEVEL_LABELS[level] || level;
}

export function levelBadgeClass(level) {
  return LEVEL_COLORS[level] || 'bg-slate-100 text-slate-700 border-slate-300';
}

export const RESTRICTION_LABELS = {
  PAID_LEAVE: 'Paid Leave',
  SAT_WFH: 'Saturday WFH',
  PARTIAL_DAY: 'Partial Day',
  SPECIAL_INCENTIVE: 'Special Incentive',
  REGULAR_INCENTIVE: 'Regular Incentive',
  OUTINGS: 'Outings',
  ACTIVITIES: 'Activities',
  TOGETHER_MILES: 'Together Miles',
};

export function restrictionLabel(code) {
  return RESTRICTION_LABELS[code] || code;
}

export function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// Fixed DD MMM YYYY regardless of the browser's locale (unlike toLocaleDateString()).
export function fmtDateOnly(iso) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export const NOTICE_STATUS_LABELS = {
  ACTIVE: 'Active',
  LAPSED: 'Lapsed',
  EXECUTED: 'Executed',
  SUPERSEDED: 'Carried forward',
};

// A Yellow or Orange card is also a Performance Improvement Plan and notice of
// termination; a Red card with no earlier Yellow/Orange also carries its own notice
// (no CAP, since Red has none). `audience` swaps first-person ("your") wording for the
// employee's own view to third-person ("the employee's") wording on HR/MD screens.
// Returns null for cards with no notice (e.g. a Logged Warning) or whose notice has
// already been carried forward to a later, escalated card.
export function noticeLine(card, audience = 'employee') {
  if (!card || !card.notice_start_date || card.notice_status === 'SUPERSEDED') return null;
  const date = fmtDateOnly(card.notice_start_date);
  const possessive = audience === 'employee' ? 'your' : "the employee's";
  if (card.notice_status === 'EXECUTED') return `Notice period counted from ${date} (executed on exit).`;
  if (card.notice_status === 'LAPSED') return `Notice period from ${date} has lapsed — no longer in effect.`;
  if (card.level === 'RED') return `This Red card carries notice of termination. ${possessive === 'your' ? 'Your' : "The employee's"} notice period counts from ${date}.`;
  return `This card is a Performance Improvement Plan and notice of termination. If the CAP is not met, ${possessive} notice period counts from ${date}.`;
}

export const CHANNEL_LABELS = {
  OFFICE: 'In the office', EMAIL: 'Email', PHONE: 'Phone call',
  WHATSAPP_WECHAT: 'WhatsApp or WeChat', MEETING: 'Meeting (in person or online)',
  TRADEIQ_CRM: 'Trade IQ or CRM', OTHER: 'Other',
};

export const IMPACT_LABELS = {
  MONEY: 'Money lost or extra cost', DEAL: 'Deal lost or delayed',
  RELATIONSHIP: 'Customer or vendor relationship hurt', TEAM_DELAY: 'Work delayed for the team',
  COMPLIANCE: 'Compliance or legal risk', REPUTATION: 'Company reputation', NONE: 'No real impact',
};

// Round-27 fix: a new case (channel_code present) shows channel/impact as readable
// ticks; an old case (no channel_code — reported before this round) keeps showing its
// stored free-text channel/what_it_caused/impact_text exactly as it was, never rewritten.
export function isNewChannelImpactCase(item) {
  return !!item?.channel_code;
}

export function channelDisplayLabel(item) {
  if (item.channel_code === 'OTHER') return item.channel_other || item.channel;
  return CHANNEL_LABELS[item.channel_code] || item.channel;
}

export function impactDisplayTags(item) {
  return (item.impact_codes || []).map((c) => IMPACT_LABELS[c] || c);
}
